import { Form, Formik, FormikHelpers } from 'formik'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../../../../../firebase'
import { FC, useEffect, useState } from 'react'
import { sileo } from 'sileo'
import { AxiosError } from 'axios'
import * as Yup from 'yup'
import { BaseFormCreateUser, Step1Values } from '../../components/BaseFormCreateUser'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutationAcceptInvitation } from '../../../../../queries/userQueries'
import { UserAuth } from '../../../../../context/AuthContext'
import userApi from '../../../../../api/user.api'
import { FieldWithErrorMessage } from '../../../ui/FieldWithErrorMsg'
import {
  clearStoredBackendToken,
  getRegistrationPath,
  REGISTRATION_STATUS
} from '../../../../../utils/authSession'

interface LoginValues {
  email: string
  password: string
}

type EmailErrorType = 'exists' | 'invalid' | null

const loginValidationSchema = Yup.object<LoginValues>({
  email: Yup.string().email('Ingresa un correo válido').required('Ingresa tu correo'),
  password: Yup.string().required('Ingresa tu contraseña')
})

const normalizeEmailExistsResponse = (data: unknown) => {
  if (typeof data === 'boolean') return data
  if (typeof data === 'string') return data.toLowerCase() === 'true'
  if (typeof data === 'number') return data === 1
  if (data && typeof data === 'object' && 'exists' in data) {
    return Boolean((data as { exists?: unknown }).exists)
  }
  return false
}

const checkEmailExists = async (email: string) => {
  if (!email) return false
  try {
    const res = await userApi.existsByEmail(email)
    return normalizeEmailExistsResponse(res.data)
  } catch (error) {
    return false
  }
}

export const FormCompleteInvitation: FC = () => {
  const acceptInvitation = useMutationAcceptInvitation()
  const [isLoading, setIsLoading] = useState(false)
  const { login, setBackendSession } = UserAuth()
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  const emailFromUrl = urlParams.get('email') || ''
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [showExistingAccountLogin, setShowExistingAccountLogin] = useState(false)
  const [existingEmail, setExistingEmail] = useState(emailFromUrl)
  const isInvitation = Boolean(emailFromUrl)

  useEffect(() => {
    if (!emailFromUrl) {
      setIsCheckingEmail(false)
      return
    }
    let isActive = true
    setIsCheckingEmail(true)
    const validateEmail = async () => {
      try {
        const emailExists = await checkEmailExists(emailFromUrl)
        if (isActive) {
          setExistingEmail(emailFromUrl)
          setShowExistingAccountLogin(emailExists)
        }
      } finally {
        if (isActive) {
          setIsCheckingEmail(false)
        }
      }
    }
    validateEmail()
    return () => {
      isActive = false
    }
  }, [emailFromUrl])

  const getErrorMessage = (error: unknown) => {
    const fallbackMessage = (error as { message?: string })?.message
    const firebaseCode = (error as { code?: string })?.code
    if (firebaseCode === 'auth/email-already-in-use') {
      return 'Correo ya existe'
    }
    if (firebaseCode === 'auth/invalid-email') {
      return 'Correo inválido'
    }
    const axiosError = error as AxiosError<{ message?: string }>
    if (axiosError?.response?.status === 409) {
      return 'Correo ya existe'
    }
    const backendMessage = axiosError?.response?.data?.message
    if (backendMessage) {
      const normalizedMessage = backendMessage.toLowerCase()
      if (normalizedMessage.includes('user not found')) {
        return 'Usuario no encontrado'
      }
      if (normalizedMessage.includes('invitation token expired')) {
        return 'La invitación expiró'
      }
      if (normalizedMessage.includes('invitation') && normalizedMessage.includes('not found')) {
        return 'Invitación no encontrada'
      }
      return backendMessage
    }
    if (axiosError?.response?.status === 404) {
      return 'Usuario no encontrado'
    }
    return fallbackMessage ?? null
  }

  const getEmailErrorType = (error: unknown): EmailErrorType => {
    const firebaseCode = (error as { code?: string })?.code
    if (firebaseCode === 'auth/email-already-in-use') {
      return 'exists'
    }
    if (firebaseCode === 'auth/invalid-email') {
      return 'invalid'
    }
    const axiosError = error as AxiosError
    if (axiosError?.response?.status === 409) {
      return 'exists'
    }
    return null
  }

  const getLoginErrorMessage = (response?: { status?: number; message?: string }) => {
    if (response?.status === 400) {
      return 'Correo inválido'
    }
    if (response?.status === 401) {
      return 'Correo o contraseña incorrectos'
    }
    if (response?.status === 404) {
      return 'Usuario no encontrado'
    }
    if (response?.status === 500) {
      return 'Algo ha pasado, contacta a soporte técnico'
    }
    return response?.message ?? 'Error inesperado.'
  }

  const handleExistingAccountLogin = async (
    values: LoginValues,
    actions: FormikHelpers<LoginValues>
  ) => {
    setIsLoading(true)
    try {
      clearStoredBackendToken()
      const res = await login(values.email, values.password)
      if (res?.status === 200) {
        if (
          res.registrationStatus &&
          res.registrationStatus !== REGISTRATION_STATUS.COMPLETED
        ) {
          sileo.info({
            title: 'Configuración Pendiente',
            description: (
              <span className='text-blue-500/80 font-medium'>
                Necesitas asignar una organización antes de continuar.
              </span>
            )
          })
          navigate(getRegistrationPath(res.registrationStatus), { replace: true })
          return
        }
        sileo.success({
          title: 'Bienvenido',
          description: (
            <span className='text-green-500/80 font-medium'>Sesión iniciada con éxito</span>
          )
        })
        navigate('/', { replace: true })
        return
      }
      const message = getLoginErrorMessage(res)
      if (res?.status === 401) {
        actions.setFieldError('password', message)
      } else {
        actions.setFieldError('email', message)
      }
      sileo.error({
        title: 'Error de Inicio',
        description: <span className='text-red-500/80 font-medium'>{message}</span>
      })
    } finally {
      actions.setSubmitting(false)
      setIsLoading(false)
    }
  }

  const step1InitialValues: Step1Values = {
    firstname: '',
    lastname: '',
    phoneNumber: '',
    email: emailFromUrl,
    password: '',
  }

  const loginInitialValues: LoginValues = {
    email: existingEmail,
    password: ''
  }

  const handleSlideNextStep = async (values: Step1Values, actions: FormikHelpers<Step1Values>) => {
    setIsLoading(true)
    const formValues = {
      ...values,
      phoneNumber: values.phoneNumber?.startsWith('+')
        ? values.phoneNumber.slice(1)
        : values.phoneNumber
    }

    try {
      if (!isInvitation) {
        const emailExists = await checkEmailExists(formValues.email)
        if (emailExists) {
          setExistingEmail(formValues.email)
          setShowExistingAccountLogin(true)
          sileo.info({
            title: 'Usuario Existente',
            description: (
              <span className='text-blue-500/80 font-medium'>
                Este correo ya tiene cuenta. Inicia sesión.
              </span>
            )
          })
          return
        }
      }
      await sileo.promise(handleCreateUserAccount(formValues), {
        loading: { title: 'Creando cuenta' },
        success: {
          title: 'Cuenta Creada',
          description: <span className='text-green-500/80 font-medium'>Estamos Listos</span>
        },
        error: {
          title: 'Error de Creación',
          description: 'No se pudo crear el usuario'
        }
      })
      sileo.success({
        title: '¡Éxito!',
        description: <span className='text-green-500/80 font-medium'>Bienvenido a Luca</span>
      })
    } catch (error) {
      const emailErrorType = getEmailErrorType(error)
      if (emailErrorType === 'exists') {
        setExistingEmail(formValues.email)
        setShowExistingAccountLogin(true)
        sileo.info({
          title: 'Cuenta Existente',
          description: (
            <span className='text-blue-500/80 font-medium'>
              Este correo ya tiene cuenta. Inicia sesión.
            </span>
          )
        })
        return
      }
      if (emailErrorType === 'invalid') {
        actions.setFieldError('email', getErrorMessage(error) ?? 'Correo inválido')
      }
    } finally {
      actions.setSubmitting(false)
      setIsLoading(false)
    }
  }

  const handleCreateUserAccount = async (values: Step1Values) => {
    // 1) Crear usuario en Firebase
    const userCredential = await createUserWithEmailAndPassword(auth, values.email, values.password)
    const firebaseToken = await userCredential.user.getIdToken()

    // 2) Aceptar invitación en backend con token de Firebase + invitationToken
    const payload = {
      email: values.email,
      firebaseToken,
      invitationToken: urlParams.get('token') ?? '',
      firstname: values.firstname,
      lastname: values.lastname,
      phoneNumber: values.phoneNumber
    }
    const res = await acceptInvitation.mutateAsync(payload)

    // 3) Normalizar token que devuelve backend (dev/prod)
    const backendToken =
      (res as any)?.token ??
      (res as any)?.accessToken ??
      (res as any)?.data?.token ??
      (res as any)?.data?.accessToken ??
      ''

    if (!backendToken) {
      // Evitamos navegar sin token: en prod causaba 401 en /v1/business
      throw new Error('No recibimos token del backend al aceptar la invitación')
    }

    // 4) Persistir + inyectar ANTES de navegar
    setBackendSession(
      { token: backendToken, registrationStatus: REGISTRATION_STATUS.COMPLETED },
      REGISTRATION_STATUS.COMPLETED,
      userCredential.user
    )

    // 5) Navegar con la sesión completa
    navigate('/', { replace: true })
  }

  return (
    <div className='relative mx-auto w-full max-w-[570px] min-h-[460px] bg-white rounded-lg shadow-md px-5 py-7 sm:px-7 sm:py-9'>
      {isCheckingEmail ? (
        <div className='flex items-center justify-center min-h-[220px] text-gray-500'>
          Verificando correo...
        </div>
      ) : showExistingAccountLogin ? (
        <Formik
          enableReinitialize
          validationSchema={loginValidationSchema}
          initialValues={loginInitialValues}
          onSubmit={handleExistingAccountLogin}
        >
          {({ isSubmitting }) => (
            <Form className='px-1'>
              <div className='mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4'>
                <div className='flex items-center justify-center gap-2'>
                  <span className='h-2.5 w-2.5 rounded-full bg-accent1' />
                  <span className='text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500'>
                    Cuenta existente
                  </span>
                </div>
                <h3 className='mt-2 text-3xl font-bold text-center text-slate-900'>
                  Inicia sesión
                </h3>
                <p className='mt-1 text-center text-sm text-slate-500'>
                  Este correo ya tiene cuenta. Ingresa tu contraseña para continuar.
                </p>
              </div>
              <ul className='grid grid-cols-2 gap-x-8 gap-y-3'>
                <li className='col-span-2'>
                  <FieldWithErrorMessage
                    disabled
                    name='email'
                    type='email'
                    placeholder='Ingresa tu correo'
                    label='CORREO'
                  />
                </li>
                <li className='col-span-2'>
                  <FieldWithErrorMessage
                    name='password'
                    type='password'
                    placeholder='Ingresa tu contraseña'
                    label='CONTRASEÑA'
                  />
                </li>
              </ul>

              <div className='flex flex-col gap-2 mt-6 w-2/3 mx-auto'>
                <div>
                  <button
                    disabled={isSubmitting}
                    type='submit'
                    className='btn-primary rounded-xl'
                  >
                    {isLoading ? <span>Ingresando...</span> : <span>Entrar</span>}
                  </button>
                </div>
              </div>

              <br />
              <p className='text-center'>
                ¿Olvidaste tu contraseña?{' '}
                <Link to='/auth/reset-password' className='text-accent1'>
                  Recuperar
                </Link>
              </p>
            </Form>
          )}
        </Formik>
      ) : (
        <BaseFormCreateUser
          title='Completa tu registro en Luca'
          initialValues={step1InitialValues}
          onSubmit={handleSlideNextStep}
          isLoading={isLoading}
          isInvitation={isInvitation}
        />
      )}
    </div>
  )
}
